import React, { useCallback, useEffect, useState } from 'react';
import type { Ctx } from '../ctx';
import { ask } from '../dialogs';
import { readUpload, saveBlob } from '../files';
import { ErrorBanner, Field, Modal, Pill } from '../ui';

interface Alert { id: string; at: string | null; level: number; ruleId: string; title: string; device: string }
interface Siem { configured: boolean; connected: boolean; lab: boolean; total: number; alerts: Alert[];
  daily: { day: string; status: 'ok' | 'error'; total: number | null; severe: number | null;
    agentsTotal: number | null; agentsActive: number | null; agentsExpected: number | null;
    generatedAt: string; error: string | null } | null }
interface Approval { mandateName: string | null; dpaName: string | null;
  workersName: string | null; approvedAt: string | null }
type DocumentKind = 'mandate' | 'dpa' | 'workers';
interface SignView {
  internal: boolean;
  defaults: { companyName: string; piva: string; sede: string; signerName: string; signerRole: string; signerEmail: string };
  request: { id: number; status: 'attesa' | 'firmata'; signerName: string; signerRole: string; signerEmail: string; createdAt: string;
    expiresAt: string; emailedAt: string | null; openedAt: string | null; signedAt: string | null; expired: boolean; link: string | null } | null;
}
const day = (iso: string) => new Date(iso).toLocaleDateString('it-IT', { day: 'numeric', month: 'short' });
const dayTime = (iso: string) => new Date(iso).toLocaleString('it-IT', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function SecuritySiem({ ctx, siteId, active, onChanged }: {
  ctx: Ctx; siteId: number; active: boolean; onChanged: () => Promise<void>
}) {
  const [state, setState] = useState<Siem | null>(null);
  const [approval, setApproval] = useState<Approval | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [sign, setSign] = useState<SignView | null>(null);
  const [form, setForm] = useState<(SignView['defaults'] & { sendEmail: boolean }) | null>(null);
  const [copied, setCopied] = useState(false);
  const refresh = useCallback(async () => {
    setBusy(true);
    const base = `/api/guard/sites/${siteId}/siem`;
    try {
      const [siem, docs, online] = await Promise.allSettled([
        ctx.api.get<Siem>(base), ctx.api.get<Approval>(`${base}/approval`), ctx.api.get<SignView>(`${base}/sign`),
      ]);
      if (siem.status === 'fulfilled') setState(siem.value);
      if (docs.status === 'fulfilled') setApproval(docs.value);
      if (online.status === 'fulfilled') setSign(online.value);
      const failed = siem.status === 'rejected' ? siem.reason : docs.status === 'rejected' ? docs.reason : null;
      setError(failed ? (failed as Error).message : null);
    } catch (e) { setError((e as Error).message); }
    finally { setBusy(false); }
  }, [ctx.api, siteId]);
  useEffect(() => { void refresh(); }, [refresh]);

  const base = `/api/guard/sites/${siteId}/siem`;
  const act = async (job: () => Promise<unknown>) => {
    setBusy(true); setError(null);
    try { await job(); await refresh(); await onChanged(); }
    catch (e) { setError((e as Error).message); }
    finally { setBusy(false); }
  };
  const upload = (kind: DocumentKind, file?: File) => {
    if (!file) return;
    void act(async () => {
      const draft = await readUpload(file);
      await ctx.api.post(`${base}/approval/${kind}`, { name: draft.name, contentBase64: draft.contentBase64 });
    });
  };
  const download = (kind: DocumentKind, name: string) => void act(async () => {
    saveBlob(await ctx.api.download(`${base}/approval/${kind}`), name);
  });
  const draft = (kind: DocumentKind, label: string, ext: 'docx' | 'pdf') => void act(async () => {
    saveBlob(await ctx.api.download(`/api/guard/sites/${siteId}/siem/draft/${kind}.${ext}`), `${label} - bozza.${ext}`);
  });
  const createSign = () => {
    if (!form) return;
    void act(async () => {
      setSign(await ctx.api.post<SignView>(`${base}/sign`, form));
      setForm(null);
    });
  };
  const copyLink = (link: string) => void navigator.clipboard.writeText(link).then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000); });
  const req = sign?.request ?? null;
  const pending = req?.status === 'attesa' && !req.expired ? req : null;
  const waText = (link: string) => `https://wa.me/?text=${encodeURIComponent(`Buongiorno, ecco i documenti per la sicurezza informatica (Sentinel) da firmare online. Si firmano dal telefono in pochi minuti: ${link}`)}`;
  const formOk = !!form && form.companyName.trim().length >= 2 && form.piva.trim().length >= 5 && form.sede.trim().length >= 5
    && form.signerName.trim().length >= 3 && form.signerRole.trim().length >= 2 && EMAIL_RE.test(form.signerEmail.trim());
  const files: { kind: DocumentKind; label: string; name: string | null }[] = [
    { kind: 'mandate', label: 'Mandato per i controlli', name: approval?.mandateName ?? null },
    { kind: 'dpa', label: 'Accordo sui dati', name: approval?.dpaName ?? null },
    { kind: 'workers', label: 'Passaggi per i lavoratori', name: approval?.workersName ?? null },
  ];

  return <section style={{ border: '1px solid var(--line)', borderRadius: 12, padding: 18, margin: '20px 0', background: 'var(--white)' }}>
    <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
      <h3 className="guard-h grow" style={{ margin: 0 }}>Registri di sicurezza</h3>
      <Pill tone={active ? 'good' : 'warn'}>{active ? 'Riepiloghi attivi' : 'Riepiloghi spenti'}</Pill>
      <button type="button" className="btn small" disabled={busy} onClick={() => void refresh()}>{busy ? 'Controllo…' : 'Aggiorna'}</button>
    </div>
    <p className="muted small" style={{ margin: '8px 0 14px', paddingBottom: 14, borderBottom: '1px solid var(--line)' }}>
      {state?.connected ? `Server raggiungibile. ${state.total} avvisi associati a questa azienda nelle ultime 24 ore.` : 'Il collegamento con il SIEM è in preparazione.'}
    </p>
    <details style={{ marginBottom: 12 }} open={!approval?.approvedAt}>
      <summary><b>Documenti per i riepiloghi SIEM</b> · <Pill tone={approval?.approvedAt ? 'good' : 'warn'}>{approval?.approvedAt ? 'Verificati' : 'Da completare'}</Pill></summary>
      <div className="sign-box">
        {pending ? <>
          <div><b>In attesa della firma</b> di {pending.signerName} · {pending.signerEmail}</div>
          <div className="muted small">Link valido fino al {day(pending.expiresAt)}
            {pending.emailedAt ? ` · email inviata il ${dayTime(pending.emailedAt)}` : ''}
            {pending.openedAt ? ` · aperto il ${dayTime(pending.openedAt)}` : ' · non ancora aperto'}</div>
          <div className="sign-actions">
            <button type="button" className="btn small dark" disabled={busy} onClick={() => copyLink(pending.link!)}>{copied ? 'Copiato ✓' : 'Copia link'}</button>
            <button type="button" className="btn small" disabled={busy} title={`Manda il link a ${pending.signerEmail} dalla tua casella`}
              onClick={() => void act(async () => setSign(await ctx.api.post<SignView>(`${base}/sign/email`, {})))}>Invia per email</button>
            <a className="btn small" href={waText(pending.link!)} target="_blank" rel="noreferrer" title="Apre WhatsApp con il messaggio e il link già scritti">WhatsApp</a>
            <a className="btn small ghost" href={pending.link!} target="_blank" rel="noreferrer" title="Apre la pagina di firma: firma tu se sei il firmatario">Apri</a>
            <button type="button" className="btn small ghost" disabled={busy}
              onClick={() => void act(async () => { if (await ask('Annullare il link? Chi lo apre non potrà più firmare.')) setSign(await ctx.api.del<SignView>(`${base}/sign`)); })}>Annulla</button>
          </div>
        </> : <div className="sign-start">
          <div className="grow">
            {req?.status === 'firmata' && req.signedAt
              ? <><b>Firmati online ✓</b><div className="muted small">Da {req.signerName} il {dayTime(req.signedAt)}. I tre PDF firmati sono qui sotto.</div></>
              : <><b>Firma online</b><div className="muted small">{req?.expired ? 'Il link precedente è scaduto. ' : ''}Il cliente legge e firma i tre documenti dal telefono o dal computer, con un codice via email: niente stampa, niente caricamenti.</div></>}
          </div>
          <button type="button" className={`btn${req?.status === 'firmata' ? ' small' : ' dark'}`} disabled={busy || !sign}
            onClick={() => sign && setForm({ ...sign.defaults, sendEmail: true })}>{req?.status === 'firmata' ? 'Nuova firma' : 'Fai firmare online'}</button>
        </div>}
      </div>
      <p className="muted small">Oppure scarica le bozze, falle firmare a mano e carica i PDF. I riepiloghi restano spenti finché Tecnoside non verifica i documenti e il numero dei PC.</p>
      <div className="doc-list">
        {files.map((file) => <div key={file.kind} className={`doc-row${file.name ? ' done' : ''}`}>
          <span className={`dot ${file.name ? 'good' : 'warn'}`} />
          <span className="doc-name">{file.label}</span>
          {file.name ? <button type="button" className="btn ghost small doc-file" title="Scarica il PDF caricato" onClick={() => download(file.kind, file.name!)}>{file.name}</button>
            : <span className="muted small doc-file">Manca: carica il PDF</span>}
          <button type="button" className="btn ghost small" disabled={busy} title="Bozza da modificare in Word, già compilata con i dati dell’azienda" onClick={() => draft(file.kind, file.label, 'docx')}>Bozza Word</button>
          <button type="button" className="btn ghost small" disabled={busy} title="La stessa bozza in PDF" onClick={() => draft(file.kind, file.label, 'pdf')}>PDF</button>
          <label className="btn small" style={{ cursor: 'pointer' }} title={file.name ? 'Sostituisce il PDF già caricato' : 'Sceglie il PDF dal computer'}>{file.name ? 'Sostituisci' : 'Carica PDF'}
            <input type="file" accept="application/pdf,.pdf" hidden disabled={busy} onChange={(e) => upload(file.kind, e.target.files?.[0])} />
          </label>
        </div>)}
      </div>
      <div style={{ display: 'flex', gap: 8, marginTop: 14, paddingTop: 14, borderTop: '1px solid var(--line)', flexWrap: 'wrap' }}>
        <button type="button" className="btn small" disabled={busy || !approval?.mandateName || !approval.dpaName || !approval.workersName || !!approval.approvedAt}
          onClick={() => void act(async () => {
            if (await ask('Hai letto i tre PDF, verificato firme, sistemi autorizzati, passaggi per i lavoratori e numero dei PC?'))
              await ctx.api.post(`${base}/approve`, {});
          })}>Segna verificati</button>
        {approval?.approvedAt && <button type="button" className="btn small" disabled={busy}
          onClick={() => void act(async () => {
            if (await ask('Vuoi revocare l’approvazione? I riepiloghi si fermano; il tecnico deve fermare gli agenti sui PC.'))
              await ctx.api.post(`${base}/revoke`, {});
          })}>Revoca approvazione</button>}
      </div>
      {approval?.approvedAt && <p className="muted small">Documenti verificati. Accendi i riepiloghi in «Servizi attivi» quando i PC sono pronti.</p>}
    </details>
    {state?.daily && <p className="small" style={{ margin: '0 0 12px' }}>
      <b>Rapporto del {new Date(state.daily.day + 'T12:00:00').toLocaleDateString('it-IT')}:</b>{' '}
      {state.daily.total == null ? 'Rapporto non disponibile: controllare il SIEM.'
        : `${state.daily.total} avvisi, ${state.daily.severe} gravi. ` +
          (state.daily.agentsTotal == null ? 'Collegamento dei PC non verificato.'
            : `${state.daily.agentsActive} di ${state.daily.agentsExpected ?? '?'} PC previsti collegati. `) +
          (state.daily.error ?? '')}
    </p>}
    <ErrorBanner message={error} onClose={() => setError(null)} />
    {form && <Modal title="Fai firmare online" narrow onClose={() => setForm(null)}
      footer={<button type="button" className="btn dark" disabled={busy || !formOk} onClick={createSign}>{form.sendEmail ? 'Crea il link e invia' : 'Crea il link'}</button>}>
      <p className="muted small" style={{ marginTop: 0 }}>Il firmatario riceve un link: fa alcune scelte, legge i tre documenti già compilati, firma col dito o col mouse e conferma con un codice via email. I PDF firmati arrivano qui da soli.{sign?.internal ? ' Per Tecnoside l’accordo sui dati diventa una dichiarazione interna.' : ' Tecnoside firma con il timbro a nome di chi crea il link.'}</p>
      <Field label="Ragione sociale"><input value={form.companyName} onChange={(e) => setForm({ ...form, companyName: e.target.value })} /></Field>
      <Field label="Partita IVA"><input value={form.piva} onChange={(e) => setForm({ ...form, piva: e.target.value })} /></Field>
      <Field label="Sede legale"><input value={form.sede} placeholder="Via, numero, CAP, città" onChange={(e) => setForm({ ...form, sede: e.target.value })} /></Field>
      <Field label="Chi firma" hint="Il legale rappresentante o chi ha i poteri per firmare."><input value={form.signerName} placeholder="Nome e cognome" onChange={(e) => setForm({ ...form, signerName: e.target.value })} /></Field>
      <Field label="Ruolo"><input value={form.signerRole} placeholder="Ad esempio Amministratore Unico" onChange={(e) => setForm({ ...form, signerRole: e.target.value })} /></Field>
      <Field label="Email di chi firma" hint="Riceve il link e il codice per confermare la firma."><input type="email" value={form.signerEmail} onChange={(e) => setForm({ ...form, signerEmail: e.target.value })} /></Field>
      <label className="small" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <input type="checkbox" checked={form.sendEmail} onChange={(e) => setForm({ ...form, sendEmail: e.target.checked })} /> Manda subito il link per email dalla mia casella
      </label>
    </Modal>}
    {state?.connected && state.alerts.length === 0 && <p className="small" style={{ marginTop: 14, paddingTop: 14, borderTop: '1px solid var(--line)' }}>Nessun evento ricevuto per questa azienda. Questo non significa che i PC siano già controllati.</p>}
    {state?.alerts.map((alert) => <div key={alert.id} style={{ borderTop: '1px solid var(--line)', padding: '8px 0' }}>
      <b>{alert.title || `Regola ${alert.ruleId}`}</b>
      <div className="muted small">{alert.device || 'PC non indicato'} · livello {alert.level} · {alert.at ? new Date(alert.at).toLocaleString('it-IT') : 'data non disponibile'}</div>
    </div>)}
  </section>;
}
